import { Flex, Text, Spacer } from "@hackthenorth/north";
import { Button, TextArea, Modal } from "north.js";
import React, { useState, useEffect } from "react";
import { styled } from "twin.macro";

import { TagInput } from "src/shared/components";
import { MENTORSHIP_TOPICS } from "src/shared/constants/mentorship";
import { MentorshipRequest } from "src/shared/contexts/MentorshipRequestContext/types";

interface RequestHostModalProps {
  isOpen: boolean;
  onSubmit: (
    title: string,
    description: string,
    categories: string[],
    priority: number
  ) => void;
  onCancel: () => void;
  requestToEdit?: MentorshipRequest;
  newRequest: boolean;
}

export const RequestHostModal: React.FC<RequestHostModalProps> = ({
  isOpen,
  onSubmit,
  onCancel,
  requestToEdit,
  newRequest,
}) => {
  const [title, setTitle] = useState("Hackathon Host Request");
  const [description, setDescription] = useState("");
  const [categories, setCategories] = useState<string[]>(["Hackathon Host"]);
  const [priority, setPriority] = useState(1); // Default to Medium priority

  useEffect(() => {
    setTitle(requestToEdit?.title ?? "Hackathon Host Request");
    setDescription(requestToEdit?.description ?? "");
    setCategories(requestToEdit?.category ?? ["Hackathon Host"]);
    setPriority(requestToEdit?.priority ?? 1); // Default to Medium priority
  }, [isOpen, requestToEdit]);

  const isValid = !!title && !!description;

  return (
    <Modal open={isOpen} onClose={onCancel} tw="max-width[800px]! p-40!">
      <Heading1Text>Request a hackathon host</Heading1Text>
      <Text mods="grey">
        A hackathon host is intended to support beginner teams navigate a
        hackathon. They will provide guidance on where to get started early on
        and empower you to submit a project by mentoring you throughout the
        whole weekend. Only 1 hackathon host request is available per hacker,
        but you&apos;ll be able to edit this request before being matched.
      </Text>
      <Spacer height={12} />
      <Text mods="bold textBody">Description </Text>
      <Spacer height={12} />
      <StyledTextArea
        fullWidth
        placeholder="Include your team name, describe your experience/interests, what kind of support you're looking for in a host, what kind of projects you might be interested in..."
        maxChars={500}
        value={description}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          setDescription(e.target.value);
        }}
        displayLimit
      />
      <Spacer height={24} />
      <OptionalQuestionWrapper>
        <Text mods="bold textBody">Category</Text>
        <OptionalText>Optional</OptionalText>
      </OptionalQuestionWrapper>
      <Spacer height={12} />
      <TagInput
        multi
        options={MENTORSHIP_TOPICS}
        selectedOptions={categories}
        setSelectedOptions={(selectedOptions) => {
          setCategories(selectedOptions);
          console.log(selectedOptions);
        }}
      />
      <Spacer height={12} />
      <div tw="flex justify-end">
        <>
          <Button color="tertiary" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <Spacer width={16} />
          <Button
            color="primary"
            size="lg"
            disabled={!isValid}
            onClick={() => {
              onSubmit(title, description, categories, priority);
            }}
          >
            {newRequest ? "Submit" : "Save"}
          </Button>
        </>
      </div>
    </Modal>
  );
};

const Heading1Text = styled.h1`
  margin-top: 0;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-weight: 900;
  font-size: 28px;
  ${({ theme }) => theme.mediaQueries.largeMobile`
    font-size: 24px;
  `}
  ${({ theme }) => theme.mediaQueries.mobile`
    font-size: 20px;
  `}
`;

const OptionalText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  font-weight: 500;
  font-style: italic;
  line-height: 1.35;
  margin-top: 4px;
`;

const OptionalQuestionWrapper = styled(Flex).attrs({ align: "center" })`
  gap: 8px;
`;

const StyledTextArea = styled(TextArea)`
  height: 150px;
`;
